import { useRef } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import { ApacUiComponent } from "../../context";
import { useApacUiStyles } from "../../hooks";
import defaultStyles from "./NumberSelector.module.scss";

interface NumberSelectorProps {
  className?: string;
  decrementButton: {
    className?: string;
    ariaLabel: string;
  };
  incrementButton: {
    className?: string;
    ariaLabel: string;
  };
  defaultValue?: number;
  minimumValue?: number;
  maximumValue?: number;
  onChange: (num: number) => void;
}
export const NumberSelector = (props: NumberSelectorProps) => {
  const {
    className,
    defaultValue,
    minimumValue = Number.MIN_SAFE_INTEGER,
    maximumValue = Number.MAX_SAFE_INTEGER,
    onChange,
    decrementButton,
    incrementButton,
  } = props;
  const styles = useApacUiStyles(ApacUiComponent.NumberSelector, defaultStyles);
  const [block, cn] = useModuleBEM(styles, ApacUiComponent.NumberSelector);

  const count = useRef<number | undefined>(defaultValue);
  const updateCount = (num: number) => {
    if (num === 0) {
      count.current = 0;
    } else if (num > 0 && num < maximumValue) {
      count.current = num;
    } else if (num > minimumValue) {
      count.current = num;
    }
    if (count.current != null) {
      onChange(count.current);
    }
  };

  return (
    <div className={clsx(block, className)}>
      <button
        aria-label={decrementButton.ariaLabel}
        className={clsx(cn("counter-button"), decrementButton.className)}
        onClick={() => updateCount((count.current ?? 0) - 1)}
        disabled={minimumValue != null && (count.current ?? 0) <= minimumValue}
      >
        <Icon iconName={IconNameEnum.minus} />
      </button>

      <div className={cn("count")}>
        {count.current ?? ""}
        <span className={"sr-only"} aria-live="polite" aria-atomic={true}>
          `{count.current ?? ""}`
        </span>
      </div>

      <button
        aria-label={incrementButton.ariaLabel}
        className={clsx(cn("counter-button"), incrementButton.className)}
        onClick={() => updateCount((count.current ?? 0) + 1)}
        disabled={maximumValue != null && (count.current ?? 0) >= maximumValue}
      >
        <Icon iconName={IconNameEnum.plus} />
      </button>
    </div>
  );
};
